import StepShell from "./StepShell";
import { fmtMoney } from "../../config";

const PRESETS = [
  { value: "0", label: "$0" },
  { value: "1000", label: "$1,000" },
  { value: "2500", label: "$2,500" },
  { value: "5000", label: "$5,000" },
];

/**
 * Step 4 — Money Down.
 *
 * Quick presets, a Custom amount input, and a Not-sure-yet escape hatch.
 * Captured as state.money_down — the wizard later resolves it to a
 * numeric amount that feeds the deal calc.
 */
export default function MoneyDownStep({ byod, onContinue, onBack, firstName }) {
  const md = byod.state.money_down || {};
  const setMD = (patch) => byod.update("money_down", patch);
  const canContinue = !!md.choice && !(md.choice === "custom" && !Number(md.custom_amount || 0));

  return (
    <StepShell
      title={firstName ? `${firstName}, how much are you thinking about putting down?` : "How much are you thinking about putting down?"}
      subtitle="More down means a lower amount financed and a smaller payment — but it isn't always required."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!canContinue}
      testIdPrefix="byod-money-down-step"
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
        {PRESETS.map((p) => {
          const sel = md.choice === p.value;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setMD({ choice: p.value, custom_amount: "" })}
              className={`rounded-2xl border-2 p-4 text-center transition ${sel ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
              data-testid={`byod-money-down-${p.value}`}
              aria-pressed={sel}
            >
              <div className="text-xl sm:text-2xl font-extrabold text-[var(--ssc-navy)]">{p.label}</div>
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <button
          type="button"
          onClick={() => setMD({ choice: "custom" })}
          className={`rounded-2xl border-2 p-4 text-left transition ${md.choice === "custom" ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
          data-testid="byod-money-down-custom-toggle"
          aria-pressed={md.choice === "custom"}
        >
          <div className="text-sm font-extrabold text-[var(--ssc-navy)]">Custom amount</div>
          <p className="text-xs text-[var(--ssc-ink-2)] mt-1">Type the exact amount you have in mind.</p>
        </button>
        <button
          type="button"
          onClick={() => setMD({ choice: "not_sure", custom_amount: "" })}
          className={`rounded-2xl border-2 p-4 text-left transition ${md.choice === "not_sure" ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.06]" : "border-[var(--ssc-line)] bg-white hover:border-[var(--ssc-blue)]/40"}`}
          data-testid="byod-money-down-not-sure"
          aria-pressed={md.choice === "not_sure"}
        >
          <div className="text-sm font-extrabold text-[var(--ssc-navy)]">Not sure yet</div>
          <p className="text-xs text-[var(--ssc-ink-2)] mt-1">Scott will help figure out the right number with you.</p>
        </button>
      </div>

      {md.choice === "custom" && (
        <div className="mt-4 max-w-xs" data-testid="byod-money-down-custom-wrap">
          <label className="ssc-label">Custom down payment</label>
          <input
            inputMode="numeric"
            className="ssc-input"
            value={md.custom_amount || ""}
            onChange={(e) => setMD({ custom_amount: e.target.value })}
            placeholder="$"
            data-testid="byod-money-down-custom-input"
            autoFocus
          />
          {Number(md.custom_amount || 0) > 0 && (
            <p className="text-xs text-[var(--ssc-ink-2)] mt-1.5">
              Using <strong className="text-[var(--ssc-navy)]">{fmtMoney(Number(md.custom_amount || 0))}</strong> down.
            </p>
          )}
        </div>
      )}
    </StepShell>
  );
}
